import { LMSLayout } from "@jambushrushti/components/lms/LMSLayout";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@jambushrushti/components/ui/card";
import { Button } from "@jambushrushti/components/ui/button";
import { Link } from "react-router-dom";
import {
  BookOpen,
  ScrollText,
  Users,
  Plus,
  BookMarked,
  ArrowUpRight,
  TrendingUp,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

const summaryCards: {
  title: string;
  value: string;
  change: string;
  icon: import("lucide-react").LucideIcon;
  color: string;
  bgColor: string;
}[] = [];

const barChartData: {
  name: string;
  issued: number;
  received: number;
  overdue: number;
}[] = [];

const pieChartData: { name: string; value: number; color: string }[] = [];

export default function LMSDashboard() {
  return (
    <LMSLayout title="Dashboard">
      <div className="space-y-6">
        {/* Welcome Header */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">
              Welcome, Librarian 🙏
            </h1>
            <p className="mt-1 text-muted-foreground">
              Here's what's happening in the library today
            </p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" className="gap-2" asChild>
              <Link to="/lms/book-entry">
                <Plus className="h-4 w-4" />
                Add Book
              </Link>
            </Button>
            <Button variant="outline" className="gap-2" asChild>
              <Link to="/lms/manuscripts">
                <ScrollText className="h-4 w-4" />
                Add Manuscript
              </Link>
            </Button>
            <Button variant="hero" className="gap-2" asChild>
              <Link to="/lms/book-issue">
                <BookMarked className="h-4 w-4" />
                Issue Book
              </Link>
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          {summaryCards.map(card => (
            <Card
              key={card.title}
              className="group cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      {card.title}
                    </p>
                    <p className="mt-2 text-3xl font-bold text-foreground">
                      {card.value}
                    </p>
                    <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                      <TrendingUp className="h-3 w-3" />
                      {card.change}
                    </p>
                  </div>
                  <div className={`rounded-xl p-3 ${card.bgColor}`}>
                    <card.icon className={`h-6 w-6 ${card.color}`} />
                  </div>
                </div>
                <div className="mt-4 border-t border-border pt-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-auto p-0 text-primary hover:text-primary/80"
                  >
                    View details
                    <ArrowUpRight className="ml-1 h-3 w-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Bar Chart */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-lg">
                Issue & Receive Overview
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={barChartData}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      className="stroke-border"
                    />
                    <XAxis dataKey="name" className="text-xs" />
                    <YAxis className="text-xs" />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "hsl(var(--card))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "8px",
                      }}
                    />
                    <Bar
                      dataKey="issued"
                      fill="hsl(var(--primary))"
                      radius={[4, 4, 0, 0]}
                      name="Issued"
                    />
                    <Bar
                      dataKey="received"
                      fill="hsl(var(--accent))"
                      radius={[4, 4, 0, 0]}
                      name="Received"
                    />
                    <Bar
                      dataKey="overdue"
                      fill="hsl(var(--destructive))"
                      radius={[4, 4, 0, 0]}
                      name="Overdue"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Pie Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Library Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieChartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {pieChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "hsl(var(--card))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "8px",
                      }}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Activity */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Recent Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {[
                {
                  action: "Book Issued",
                  item: "Āgama Encyclopedia Vol. 3",
                  user: "Ramesh Patel",
                  time: "2 minutes ago",
                  type: "issue",
                },
                {
                  action: "Book Received",
                  item: "Jain Dharma Ka Itihas",
                  user: "Suresh Shah",
                  time: "15 minutes ago",
                  type: "receive",
                },
                {
                  action: "New Member",
                  item: "Dr. Meena Jain",
                  user: "Registration",
                  time: "1 hour ago",
                  type: "member",
                },
                {
                  action: "Manuscript Added",
                  item: "Kalpa Sutra (Hastrap #1247)",
                  user: "Admin",
                  time: "3 hours ago",
                  type: "manuscript",
                },
              ].map((activity, index) => (
                <div
                  key={index}
                  className="flex items-center gap-4 rounded-lg bg-secondary/30 p-3 transition-colors hover:bg-secondary/50"
                >
                  <div
                    className={`flex h-10 w-10 items-center justify-center rounded-full ${
                      activity.type === "issue"
                        ? "bg-primary/20 text-primary"
                        : activity.type === "receive"
                          ? "bg-green-500/20 text-green-500"
                          : activity.type === "member"
                            ? "bg-blue-500/20 text-blue-500"
                            : "bg-amber-500/20 text-amber-500"
                    }`}
                  >
                    {activity.type === "issue" ? (
                      <BookMarked className="h-5 w-5" />
                    ) : activity.type === "receive" ? (
                      <BookOpen className="h-5 w-5" />
                    ) : activity.type === "member" ? (
                      <Users className="h-5 w-5" />
                    ) : (
                      <ScrollText className="h-5 w-5" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-foreground">
                      {activity.action}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {activity.item}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">
                      {activity.user}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {activity.time}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </LMSLayout>
  );
}
